import { useMemo } from "react"

import { useVariantsByIds } from "~/hooks/useVariants"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

type StockWalkPreorder = NonNullable<IVariantSummary["futureKnownPreorders"]>[number] & {
  variantId: string
}

type RawPreorder = NonNullable<IVariantSummary["futureKnownPreorders"]>[number]

function isValidPreorder(
  po: RawPreorder | null | undefined,
  warehouseId?: string,
): po is RawPreorder {
  if (!po) return false
  if (warehouseId && po.warehouseId !== warehouseId) return false
  if (po.quantity === 0) return false
  return true
}

export type StockWalkEvent =
  | {
      type: "preorder"
      date: Date
      skuId: string
      warehouseId: string
      channel: string
      quantity: number
      unfulfillableQty: number
      atRiskQty: number
    }
  | { type: "incoming"; date: Date; skuId: string; warehouseId: string; quantity: number }

type UsePreorderStockWalkResult = {
  preorders: StockWalkPreorder[]
  availableAtDeliveryMap: Map<string, number>
  skuStockMap: Map<string, number>
  sortedEvents: StockWalkEvent[]
}

/**
 * Shared hook for preorder stock walk computation over the SKU store's variant data,
 * scoped to the globally selected warehouse. Consumed via usePreorderShortfallRows for
 * picker-scoped surfaces (PreorderFulfillmentCard, PreorderWhyDialog). Surfaces that
 * must be independent of the global picker use useUnfulfillablePreordersForWarehouses.
 *
 * Pass an empty variantIds array to skip computation (e.g. when a feature flag is off).
 *
 * Returns enriched preorders (with unfulfillableQty/atRiskQty already set from the store)
 * plus a stock walk map giving available stock at each preorder's delivery point.
 *
 * Key format for availableAtDeliveryMap: `${skuId}|${warehouseId}|${channel}|${deliveryDate.getTime()}`
 * Key format for skuStockMap: `${skuId}|${warehouseId}`
 */
export function usePreorderStockWalk(
  variantIds: string[],
  warehouseId?: string,
): UsePreorderStockWalkResult {
  const variants = useVariantsByIds(variantIds)

  const skuStockMap = useMemo(() => {
    const m = new Map<string, number>()
    for (const v of variants) {
      for (const lvl of v.stockLevelDetails ?? []) {
        for (const [skuId, qty] of Object.entries(lvl.skuStockLevels as Record<string, number>)) {
          const key = `${skuId}|${lvl.warehouseId}`
          m.set(key, (m.get(key) ?? 0) + qty)
        }
      }
    }
    return m
  }, [variants])

  const preorders = useMemo(() => {
    const items: StockWalkPreorder[] = []
    for (const v of variants) {
      for (const po of v.futureKnownPreorders ?? []) {
        if (!isValidPreorder(po, warehouseId)) continue
        items.push({ ...po, variantId: v.variantId })
      }
    }
    return items
  }, [variants, warehouseId])

  const { availableAtDeliveryMap, sortedEvents } = useMemo(() => {
    const stock = new Map<string, number>(skuStockMap)

    const incoming = variants.flatMap((v) =>
      (v.incomingOrdersWarehouse ?? []).flatMap((io) =>
        (io.skuItems ?? []).map((item) => ({
          skuId: item.skuId,
          quantity: item.quantity,
          warehouseId: io.warehouseId,
          date: io.expectedArrivalDate,
        })),
      ),
    )

    type Entry =
      | { type: "preorder"; date: Date; po: StockWalkPreorder }
      | { type: "incoming"; date: Date; skuId: string; quantity: number; warehouseId: string }

    const sorted: Entry[] = [
      ...preorders.map((po) => ({ type: "preorder" as const, date: po.deliveryDate, po })),
      ...incoming.map((io) => ({
        type: "incoming" as const,
        date: io.date,
        skuId: io.skuId,
        quantity: io.quantity,
        warehouseId: io.warehouseId,
      })),
    ].sort((a, b) => {
      // A preorder adds its quantity to the sort key (incoming adds 0), so on a shared date
      // incoming is credited before preorders — matching addPreOrderFulfillment's ordering.
      const keyA = a.date.getTime() + (a.type === "preorder" ? a.po.quantity : 0)
      const keyB = b.date.getTime() + (b.type === "preorder" ? b.po.quantity : 0)
      return keyA - keyB
    })

    const result = new Map<string, number>()

    for (const item of sorted) {
      const key =
        item.type === "preorder"
          ? `${item.po.skuId}|${item.po.warehouseId}`
          : `${item.skuId}|${item.warehouseId}`
      const available = stock.get(key) ?? 0
      if (item.type === "incoming") {
        stock.set(key, available + item.quantity)
      } else {
        const poKey = `${item.po.skuId}|${item.po.warehouseId}|${item.po.channel}|${item.po.deliveryDate.getTime()}`
        result.set(poKey, available)
        stock.set(key, Math.max(0, available - item.po.quantity))
      }
    }

    const events: StockWalkEvent[] = sorted.map((item) =>
      item.type === "incoming"
        ? {
            type: "incoming",
            date: item.date,
            skuId: item.skuId,
            warehouseId: item.warehouseId,
            quantity: item.quantity,
          }
        : {
            type: "preorder",
            date: item.date,
            skuId: item.po.skuId,
            warehouseId: item.po.warehouseId,
            channel: item.po.channel,
            quantity: item.po.quantity,
            unfulfillableQty: item.po.unfulfillableQty,
            atRiskQty: item.po.atRiskQty,
          },
    )

    return { availableAtDeliveryMap: result, sortedEvents: events }
  }, [preorders, variants, skuStockMap])

  return { preorders, availableAtDeliveryMap, skuStockMap, sortedEvents }
}
